import { useCallback, useState, useEffect, useContext, useRef } from "react";
import { Redirect } from "react-router-dom";

import {
  Box,
  Grid,
  Typography,
  ThemeProvider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import CheckIcon from "@mui/icons-material/Check";

import { mergeDarkTheme } from "@aclymatepackages/themes";
import {
  editObjectData,
  editRowData,
  addDataRow,
  removeDataRow
} from "@aclymatepackages/array-immutability-helpers";
import {
  Checkbox,
  TextField,
  Select,
  AnimatedLogo
} from "@aclymatepackages/atoms";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";
import { IndustryAutocomplete } from "@aclymatepackages/modules";
import { fuelCarbonFromGallons } from "@aclymatepackages/calcs/travel";

import SurveyLayout from "../survey-shape/SurveyLayout";

import {
  SECTION_IDS,
  SURVEY_SECTIONS,
  sectionForTransactionCategory
} from "./sections";
import SurveyScoutPanel from "./SurveyScoutPanel";
import SurveyCapturedSummary from "./SurveyCapturedSummary";
import { getFieldDisplayValue } from "./scoutFieldHandlers";

import * as Sentry from "@sentry/react";

import NumberInput from "../../atoms/inputs/NumberInput";
import DollarInput from "../../atoms/inputs/DollarInput";
import OrDivider from "../../atoms/OrDivider";
import Link from "../../atoms/mui/Link";
import FacilityTypeSelect from "../../inputs/offices/FacilityTypeSelect";
import GasUnitSelect from "../../inputs/offices/GasUnitSelect";
import PlacesAutocomplete from "../../inputs/autocomplete/PlacesAutocomplete";

import {
  setAddress,
  findGoogleAddressStringDetails
} from "../../../helpers/utils/geography";
import {
  fetchAddressEGrid,
  fetchFuelBillCarbon,
  fetchNaicsCodes
} from "../../../helpers/utils/apiCalls";
import { isObjectEmpty } from "../../../helpers/otherHelpers";
import { analyticsTrack } from "../../../helpers/analytics";

import { useCachedDisplayData } from "../../../helpers/firebase";
import usePermissions from "../../../helpers/hooks/usePermissions";

import { SurveyContext } from "../survey-shape/SurveyContext";
import {
  transactionsQuestions,
  extractScopeTons,
  DateRangeText,
  useOnSurveyComplete,
  SURVEY_FIELD_IDS,
  convertFuelVolumeToGallons,
  clampFormStepToBounds,
  hasSubmittedSurveyForYear
} from "./helpers";
import { NAVIGATOR_UPGRADE_PATH } from "../helpers";

import { getAvailableSurveyYears } from "../../../helpers/components/vendors";

const FormQuestionLayout = ({
  onSave,
  field,
  value,
  saveDisabled,
  onNextStep,
  onPrevStep,
  children,
  fieldId,
  allowSkip = true
}) => {
  const {
    updateAccountData,
    formStep,
    formStepsCount,
    skipQuestion,
    markUnknown,
    vendorId,
    selectedYear,
    isScoutCollapsed,
    setSurveyCompletionStatus
  } = useContext(SurveyContext);
  const onSurveyComplete = useOnSurveyComplete();
  const { canEdit } = usePermissions();

  const onSubmit = useCallback(async () => {
    const isFinalStep = formStep === formStepsCount - 2;
    if (onSave) {
      await onSave();
    }
    if (!onSave && field && value !== undefined) {
      await updateAccountData({ [field]: value });
    }
    onNextStep();
    if (isFinalStep) {
      if (setSurveyCompletionStatus) {
        setSurveyCompletionStatus("in-flight");
      }
      onSurveyComplete()
        .then(() => {
          if (setSurveyCompletionStatus) {
            setSurveyCompletionStatus("done");
          }
        })
        .catch((err) => {
          Sentry.captureException(err, {
            tags: { feature: "explorer-survey-complete", path: "form-submit" },
            extra: { surveyYear: selectedYear, vendorId }
          });
          if (setSurveyCompletionStatus) {
            setSurveyCompletionStatus("error");
          }
        });
    }
  }, [
    formStep,
    formStepsCount,
    onSave,
    field,
    value,
    updateAccountData,
    setSurveyCompletionStatus,
    onNextStep,
    onSurveyComplete,
    selectedYear,
    vendorId
  ]);

  const showSkipControls = allowSkip && !!fieldId && !!skipQuestion;

  const handleSkip = async () => {
    if (skipQuestion) {
      await skipQuestion(fieldId);
    }
    analyticsTrack("survey_field_skipped", {
      companyId: vendorId,
      surveyYear: selectedYear,
      fieldId
    });
    onNextStep();
  };

  const handleUnknown = async () => {
    if (markUnknown) {
      await markUnknown(fieldId);
    }
    analyticsTrack("survey_field_unknown", {
      companyId: vendorId,
      surveyYear: selectedYear,
      fieldId
    });
    onNextStep();
  };

  const showFormControls = isScoutCollapsed;

  return (
    <Box
      flexGrow={1}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box
        py={2}
        sx={canEdit ? {} : { pointerEvents: "none", opacity: 0.6 }}
        aria-disabled={canEdit ? undefined : true}
      >
        {children}
      </Box>
      {showFormControls && (
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Button disabled={!onPrevStep} onClick={onPrevStep}>
              Back
            </Button>
          </Grid>
          {showSkipControls && (
            <Grid item>
              <Button color="inherit" onClick={handleSkip} disabled={!canEdit}>
                Skip
              </Button>
            </Grid>
          )}
          {showSkipControls && (
            <Grid item>
              <Button
                color="inherit"
                onClick={handleUnknown}
                disabled={!canEdit}
              >
                I don't know
              </Button>
            </Grid>
          )}
          <Grid item style={{ marginLeft: "auto" }}>
            <Button
              disabled={saveDisabled || !canEdit}
              onClick={onSubmit}
              variant="contained"
            >
              Next
            </Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

const FormDateTitleLayout = ({ children, title, ...otherProps }) => (
  <FormQuestionLayout {...otherProps}>
    <Grid container direction="column" spacing={4}>
      <Grid item>
        <DateRangeText />
        <Typography variant="h3">{title}</Typography>
      </Grid>
      <Grid item>{children}</Grid>
    </Grid>
  </FormQuestionLayout>
);

const YesNoCheckboxes = ({ value, setValue }) => (
  <Grid container spacing={1} direction="column">
    <Grid item>
      <Checkbox label="Yes" value={!!value} editValue={() => setValue(true)} />
    </Grid>
    <Grid item>
      <Checkbox
        label="No"
        value={value === false}
        editValue={() => setValue(false)}
      />
    </Grid>
  </Grid>
);

const EmployeeCount = ({ employeeCount, setEmployeeCount }) => (
  <Grid container spacing={4} direction="column">
    <Grid item>
      <TextField value={employeeCount} setValue={setEmployeeCount} />
    </Grid>
    {employeeCount >= 50 && (
      <Grid item>
        <Typography variant="subtitle1" color="secondary">
          For companies with 50+ employees, we recommend our Turn Key service to
          speed up your data entry.{" "}
          <Link href="https://aclymate.com/service-tiers">Learn more</Link>
        </Typography>
      </Grid>
    )}
  </Grid>
);

const FacilitiesInputBlock = ({
  facilities,
  setFacilities,
  onNextStep,
  onPrevStep,
  onSave,
  scope1Tons,
  scope2Tons,
  selectedYear,
  fieldId
}) => {
  const [selectedOfficeIdx, setSelectedOfficeIdx] = useState(0);

  const { name, address, sqFootage, facilityType } =
    facilities[selectedOfficeIdx] || {};
  const { electricKwh, gasVolume, gasVolumeUnit } =
    (facilities[selectedOfficeIdx]?.yearlyData || {})[selectedYear] || {};

  const editFacility = (field) => (value) =>
    editRowData(selectedOfficeIdx, setFacilities)(field, value);

  const editYearlyData = (field) => (value) => {
    const currentYearlyData = facilities[selectedOfficeIdx]?.yearlyData || {};
    return editRowData(selectedOfficeIdx, setFacilities)("yearlyData", {
      ...currentYearlyData,
      [selectedYear]: {
        ...(currentYearlyData[selectedYear] || {}),
        [field]: value
      }
    });
  };

  const onNewFacility = () => {
    setSelectedOfficeIdx(facilities.length);
    return addDataRow(setFacilities, {});
  };

  const onRemoveFacility = () => {
    removeDataRow(selectedOfficeIdx, setFacilities)();
    if (!!selectedOfficeIdx) {
      return setSelectedOfficeIdx((currentIndex) => currentIndex - 1);
    }

    return;
  };

  const areAllFacilitiesComplete = facilities.reduce(
    (acc, { name, address, sqFootage, facilityType }) =>
      acc &&
      name &&
      address &&
      sqFootage &&
      numbersRegExpTest(sqFootage) &&
      facilityType,
    true
  );

  return (
    <FormDateTitleLayout
      title="Provide information on your company's facilities"
      fieldId={fieldId}
      saveDisabled={!areAllFacilitiesComplete}
      onPrevStep={onPrevStep}
      onNextStep={onNextStep}
      onSave={onSave}
    >
      <Grid container spacing={4} direction="column">
        {facilities.length > 1 && (
          <Grid item>
            <Select
              label="Select Facility to Edit"
              options={facilities.map(({ name }, idx) => ({
                label: name || `Facility #${idx + 1}`,
                value: idx
              }))}
              value={selectedOfficeIdx}
              editValue={setSelectedOfficeIdx}
            />
          </Grid>
        )}
        <Grid item container spacing={4} direction="column">
          <Grid item>
            <TextField
              label="Facility Name"
              value={name}
              setValue={editFacility("name")}
            />
          </Grid>
          <Grid item>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              {name || `Facility #${selectedOfficeIdx + 1}`} Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PlacesAutocomplete
                  label="Address"
                  place={address}
                  editPlace={setAddress(editFacility("address"))}
                  size="small"
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="Size (sq. footage)"
                  value={sqFootage}
                  setValue={editFacility("sqFootage")}
                />
              </Grid>
              <Grid item xs={7}>
                <FacilityTypeSelect
                  facilityType={facilityType}
                  editFacilityType={editFacility("facilityType")}
                />
              </Grid>
            </Grid>
          </Grid>
          {(!scope1Tons || !scope2Tons) && (
            <Grid item>
              <Typography variant="subtitle1" color="textSecondary">
                Add more details (optional)
              </Typography>
              <Grid container spacing={2}>
                {!scope2Tons && (
                  <Grid item xs={12}>
                    <TextField
                      label={`Total Electric Kwh in ${selectedYear}`}
                      value={electricKwh}
                      setValue={editYearlyData("electricKwh")}
                    />
                  </Grid>
                )}
                {!scope1Tons && (
                  <>
                    <Grid item xs={8}>
                      <TextField
                        label={`Total volume of gas in ${selectedYear}`}
                        value={gasVolume}
                        setValue={editYearlyData("gasVolume")}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <GasUnitSelect
                        gasUnit={gasVolumeUnit}
                        editGasUnit={editYearlyData("gasVolumeUnit")}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item container spacing={2} justifyContent="center">
          {facilities.length > 1 && (
            <Grid item>
              <Button onClick={onRemoveFacility} color="inherit">
                Remove This Facility
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button
              variant="contained"
              onClick={onNewFacility}
              color="secondary"
            >
              Add another facility
            </Button>
          </Grid>
        </Grid>
        {facilities.length > 1 && (
          <Grid item>
            <Alert severity="info">
              Please make sure you fill out all of the information for all of
              your facilities
            </Alert>
          </Grid>
        )}
      </Grid>
    </FormDateTitleLayout>
  );
};

const VehicleFuelQuestion = ({
  onNextStep,
  onPrevStep,
  fuelVolume,
  fuelVolumeUnit = "gallons",
  fuelDollars,
  editVehicleFuelData,
  onSave,
  fieldId
}) => (
  <FormDateTitleLayout
    onNextStep={onNextStep}
    onPrevStep={onPrevStep}
    fieldId={fieldId}
    title="How much fuel did you use for these vehicles?"
    saveDisabled={
      !(
        (fuelVolume && fuelVolumeUnit) ||
        (fuelDollars && numbersRegExpTest(fuelDollars))
      )
    }
    onSave={onSave}
  >
    <Grid container directon="column" spacing={2}>
      <Grid item container spacing={2}>
        <Grid item xs={9}>
          <TextField
            label="Total fuel for time period"
            value={fuelVolume ? Math.round(fuelVolume) : ""}
            setValue={editVehicleFuelData("fuelVolume")}
          />
        </Grid>
        <Grid item xs={3}>
          <Select
            size="small"
            options={[
              {
                label: "Gallons",
                value: "gallons"
              },
              { label: "Liters", value: "liters" }
            ]}
            value={fuelVolumeUnit}
            editValue={editVehicleFuelData("fuelVolumeUnit")}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <OrDivider />
      </Grid>
      <Grid item xs={12}>
        <DollarInput
          label="Total Amount Spent on Fuel"
          value={fuelDollars}
          setValue={editVehicleFuelData("fuelDollars")}
        />
      </Grid>
    </Grid>
  </FormDateTitleLayout>
);

const buildTransactionCategorySave =
  ({
    transaction,
    questions,
    categoryNaics,
    categoryTonsCo2ePerDollar,
    scopeThreeCategory,
    title,
    selectedYear,
    existingTransactions,
    newCollectionDoc,
    deleteCachedCollectionDoc,
    updateAccountData
  }) =>
  async () => {
    const areAllQuestionsAnswered = questions.reduce((acc, { naics }) => {
      const value = transaction[`${naics}`];
      return (!!value || value === 0) && numbersRegExpTest(value) && acc;
    }, true);
    const categoryValue = transaction[`${categoryNaics}`];

    const toDelete = existingTransactions?.filter(
      ({ scopeThreeCategory: existingCat, source, date }) => {
        const d = date?.toDate ? date.toDate() : date;
        return (
          source === "vendor-survey" &&
          existingCat === scopeThreeCategory &&
          new Date(d).getFullYear() === Number(selectedYear)
        );
      }
    );
    if (toDelete?.length) {
      await Promise.all(
        toDelete.map(({ id }) => deleteCachedCollectionDoc("transactions", id))
      );
    }

    const entries = areAllQuestionsAnswered
      ? questions.map(({ naics, label, tonsCo2ePerDollar }) => ({
          name: `${selectedYear} ${label} Spend`,
          naicsCode: naics,
          tonsCo2ePerDollar,
          value: transaction[`${naics}`]
        }))
      : [
          {
            name: `${selectedYear} ${title} Spend`,
            naicsCode: categoryNaics,
            tonsCo2ePerDollar: categoryTonsCo2ePerDollar,
            value: categoryValue
          }
        ];

    await Promise.all(
      entries
        .filter(({ value }) => value && Number(value) > 0)
        .map(({ name, naicsCode, tonsCo2ePerDollar, value }) =>
          newCollectionDoc("transactions", {
            archived: false,
            source: "vendor-survey",
            status: "confirmed",
            description: name,
            subcategory: "spend-based",
            scopeThreeCategory,
            date: new Date(selectedYear, 11, 30),
            value: Number(value),
            tonsCo2e: tonsCo2ePerDollar * Number(value),
            emissionData: { naicsCode }
          })
        )
    );

    await updateAccountData({ hasUnaggregatedTransactions: true });
  };

const TransactionsQuestionsLayout = ({
  onNextStep,
  onPrevStep,
  transaction,
  title,
  questions,
  naics: categoryNaics,
  tonsCo2ePerDollar: categoryTonsCo2ePerDollar,
  scopeThreeCategory,
  editTransactionRow,
  fieldId
}) => {
  const {
    newCollectionDoc,
    deleteCachedCollectionDoc,
    selectedYear,
    updateAccountData
  } = useContext(SurveyContext);
  const [existingTransactions] = useCachedDisplayData("transactions");

  const editTransaction = (field) => (value) =>
    editTransactionRow(field, value);

  const areAllQuestionsAnswered = questions.reduce((acc, { naics }) => {
    const value = transaction[`${naics}`];
    return (!!value || value === 0) && numbersRegExpTest(value) && acc;
  }, true);

  const categoryValue = transaction[`${categoryNaics}`];

  const onSave = buildTransactionCategorySave({
    transaction,
    questions,
    categoryNaics,
    categoryTonsCo2ePerDollar,
    scopeThreeCategory,
    title,
    selectedYear,
    existingTransactions,
    newCollectionDoc,
    deleteCachedCollectionDoc,
    updateAccountData
  });

  return (
    <FormDateTitleLayout
      title={`What were your company's total ${title.toLowerCase()} expenses?`}
      onNextStep={onNextStep}
      onPrevStep={onPrevStep}
      fieldId={fieldId}
      saveDisabled={!areAllQuestionsAnswered && !categoryValue}
      onSave={onSave}
    >
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <DollarInput
            label="Enter Total Amount"
            value={categoryValue}
            setValue={editTransaction(categoryNaics)}
          />
        </Grid>
        <Grid item>
          <OrDivider />
        </Grid>
        {questions.map(({ naics, ...otherProps }) => (
          <Grid item key={`question-input-${naics}`}>
            <DollarInput
              value={transaction[naics]}
              setValue={editTransaction(naics)}
              {...otherProps}
            />
          </Grid>
        ))}
      </Grid>
    </FormDateTitleLayout>
  );
};

const EmissionsScopesQuestionsRow = ({ idx, tons, editRow, unknown }) => (
  <Grid item container spacing={2} alignItems="center">
    <Grid item xs={7}>
      <NumberInput
        label={`Scope ${idx + 1} Emissions (tons)`}
        value={tons}
        setValue={(value) => editRow("tons", value)}
        disabled={unknown}
      />
    </Grid>
    <Grid item>
      <Button
        variant={unknown ? "contained" : "outlined"}
        color="secondary"
        endIcon={unknown && <CheckIcon />}
        onClick={() => {
          if (!unknown) {
            editRow("tons", "");
          }
          return editRow("unknown", !unknown);
        }}
      >
        I Don't Know
      </Button>
    </Grid>
  </Grid>
);

const EmissionsScopesQuestions = ({ scopeTons, setScopeTons }) => (
  <Grid container direction="column" spacing={2}>
    {scopeTons.map((scopeTonsRow, idx) => (
      <EmissionsScopesQuestionsRow
        key={`scope-tons-input-row-${idx}`}
        {...scopeTonsRow}
        idx={idx}
        editRow={editRowData(idx, setScopeTons)}
      />
    ))}
  </Grid>
);

const IntroductionContentAccordion = ({ question, answer, bullets }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((current) => !current)}
      style={{ width: "100%" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h5">{question}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body1">{answer}</Typography>
        {bullets && (
          <List>
            {bullets.map((bullet, idx) => (
              <ListItem key={`accordion-bullet-${idx}`}>
                <ListItemIcon>
                  <CircleIcon />
                </ListItemIcon>
                <ListItemText primary={bullet} />
              </ListItem>
            ))}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const IntroductionContentBlock = () => {
  const contentAccordionFaqs = [
    {
      question: "What is carbon accounting?",
      answer:
        "Carbon accounting helps your organization determine the amount of greenhouse gases (GHG) it emits. Greenhouse gases are commonly referred to as \u201Ccarbon\u201D but include multiple gases responsible for climate change, with all calculations being expressed as carbon equivalents in tonnes of carbon. This information can help you find ways to reduce, offset and report on your carbon footprint."
    },
    {
      question: "Why should I complete this survey?",
      answer:
        "Completing this emissions survey provides you with an accurate picture of your company's carbon footprint. This data helps you understand your environmental impact, find ways to reduce emissions, and report your progress to stakeholders and customers."
    },
    {
      question: "What information do I need to gather?",
      answer:
        "A great place to start is with your financial accounting from last year. You will need information about the following, for the last full calendar year:",
      bullets: [
        "The basics: industry, revenue, employees, offices and vehicles",
        "Facility and vehicle details",
        "Expenses such as travel, freight, goods and services"
      ]
    }
  ];

  return (
    <Grid container spacing={2}>
      <Grid item>
        <ThemeProvider theme={mergeDarkTheme}>
          <Typography variant="body1" paragraph color="textPrimary">
            This survey should take no longer than 10 minutes to complete once
            you've gathered the information you'll need. You can view your
            progress in the bar at the top of the page, and you can always save
            your progress and come back later to continue.
          </Typography>
        </ThemeProvider>
      </Grid>
      {contentAccordionFaqs.map((faq, idx) => (
        <Grid item key={`faq-accordion-${idx}`} xs={12}>
          <IntroductionContentAccordion {...faq} />
        </Grid>
      ))}
    </Grid>
  );
};

const MIN_THANKS_DISPLAY_MS = 6000;

const ThanksStep = () => {
  const { selectedYear, surveyCompletionStatus } = useContext(SurveyContext);
  const [redirect, setRedirect] = useState(false);
  const mountedAtRef = useRef(Date.now());

  useEffect(() => {
    if (
      surveyCompletionStatus !== "done" &&
      surveyCompletionStatus !== "error"
    ) {
      const fallbackTimer = setTimeout(() => {
        analyticsTrack("Survey Completed");
        setRedirect(true);
      }, 90000);
      return () => clearTimeout(fallbackTimer);
    }
    const elapsed = Date.now() - mountedAtRef.current;
    const remainingDelay = Math.max(0, MIN_THANKS_DISPLAY_MS - elapsed);
    const redirectTimer = setTimeout(() => {
      analyticsTrack("Survey Completed");
      setRedirect(true);
    }, remainingDelay);
    return () => clearTimeout(redirectTimer);
  }, [surveyCompletionStatus]);

  if (redirect) {
    return <Redirect to="/explorer/dashboard" />;
  }

  return (
    <Box
      style={{ height: "100%" }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="h3" align="center" gutterBottom>
            Thanks for completing your emissions profile
          </Typography>
          <Typography variant="h6" align="center">
            Summarizing your carbon emissions for {selectedYear} — you&apos;ll
            be redirected to your dashboard as soon as it&apos;s ready.
          </Typography>
        </Grid>
        <Grid item container justifyContent="center">
          <Grid item>
            <AnimatedLogo />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

const YearSelector = () => {
  const { onNextStep, selectedYear, setSelectedYear, vendorData } =
    useContext(SurveyContext);
  const { vendorSurveys = [] } = vendorData || {};

  const availableYears = getAvailableSurveyYears(vendorSurveys);
  const isUpdating =
    selectedYear &&
    vendorSurveys.some((s) => Number(s.year) === Number(selectedYear));

  return (
    <FormQuestionLayout
      onNextStep={onNextStep}
      saveDisabled={!selectedYear}
      allowSkip={false}
    >
      <Grid container spacing={4} direction="column">
        <Grid item>
          <Typography variant="h3">
            Which year would you like to complete your emissions survey for?
          </Typography>
        </Grid>
        <Grid item>
          <Select
            label="Survey Year"
            options={availableYears.map((year) => ({
              label: vendorSurveys.some((s) => Number(s.year) === Number(year))
                ? `${year} (Update existing)`
                : `${year}`,
              value: year
            }))}
            value={selectedYear}
            editValue={setSelectedYear}
          />
        </Grid>
        {isUpdating && (
          <Grid item>
            <Alert severity="info">
              You have already completed a survey for {selectedYear}. Saving
              will update your existing data for that year.
            </Alert>
          </Grid>
        )}
        <Grid item>
          <Alert severity="info">
            Each survey estimates your carbon footprint for one calendar year.
            For automated multi-year tracking and trend reporting,{" "}
            <Link href={NAVIGATOR_UPGRADE_PATH} color="secondary">
              upgrade to Aclymate Navigator
            </Link>
            .
          </Alert>
        </Grid>
        {selectedYear && (
          <Grid item>
            <DateRangeText />
          </Grid>
        )}
      </Grid>
    </FormQuestionLayout>
  );
};

const useScopeQuestionsStep = () => {
  const {
    scopeTons,
    setScopeTons,
    onNextStep,
    selectedYear,
    updateCollectionDoc,
    newCollectionDoc
  } = useContext(SurveyContext);
  const [existingEmissions] = useCachedDisplayData("emissions");

  const onScopeTonsSave = async () => {
    await Promise.all(
      scopeTons.map(({ scope, tons }) => {
        const existing = existingEmissions?.find((e) => {
          const eventDate = e.date?.toDate ? e.date.toDate() : e.date;
          return (
            e.scope === scope &&
            e.source === "vendor-survey" &&
            new Date(eventDate).getFullYear() === Number(selectedYear)
          );
        });
        if (existing) {
          return updateCollectionDoc("emissions", existing.id, {
            volume: tons
          });
        }
        return newCollectionDoc("emissions", {
          archived: false,
          scope,
          volume: tons,
          volumeUnit: "tons",
          date: new Date(selectedYear, 11, 31),
          status: "confirmed",
          name: `${selectedYear} Scope ${scope} Survey Emissions`,
          source: "vendor-survey"
        });
      })
    );
  };

  return [
    {
      fieldId: SURVEY_FIELD_IDS.scopeTons,
      title: "Emissions Totals",
      prompt: `Do you happen to already know your total carbon emissions for ${selectedYear}? Most companies don't have this on hand — totally fine, we can build it up together from your facilities, vehicles, and expenses. But if you do know any of your scope 1, 2, or 3 totals, just tell me and we'll use them as a head start.`,
      description:
        "If you already know the breakdown of your company's scope emission, you can enter them here.",
      onSave: onScopeTonsSave,
      form: (
        <FormDateTitleLayout
          title="Last Year's Total Emissions"
          fieldId={SURVEY_FIELD_IDS.scopeTons}
          saveDisabled={
            !scopeTons.reduce(
              (acc, { tons, unknown }) => (tons || unknown) && acc,
              true
            )
          }
          onSave={onScopeTonsSave}
          onNextStep={onNextStep}
        >
          <EmissionsScopesQuestions
            scopeTons={scopeTons}
            setScopeTons={setScopeTons}
          />
        </FormDateTitleLayout>
      ),
      section: SECTION_IDS.COMPANY_PROFILE
    }
  ];
};

const useVendorDataSteps = () => {
  const {
    vendorData,
    setVendorData,
    onNextStep,
    onPrevStep,
    selectedYear,
    updateAccountData
  } = useContext(SurveyContext);

  const {
    isRemote,
    revenueYears,
    address,
    hasNoCompanyVehicles,
    yearlyEmployeeCounts = {},
    yearlyIndustry = {}
  } = vendorData;

  const employeeCount =
    yearlyEmployeeCounts[selectedYear] ?? vendorData.employeeCount ?? "";
  const industry = yearlyIndustry[selectedYear] ?? vendorData.industry ?? null;

  const editCompanyData = (field) => (value) =>
    editObjectData(setVendorData, field, value);

  const revenueForYear = (revenueYears || []).find(
    ({ year }) => Number(year) === Number(selectedYear)
  );
  const revenue = revenueForYear?.revenue ?? "";
  const onRevenueEdit = (newRevenue) => {
    const otherYears = (revenueYears || []).filter(
      ({ year }) => Number(year) !== Number(selectedYear)
    );
    editCompanyData("revenueYears")([
      ...otherYears,
      { year: selectedYear, revenue: newRevenue }
    ]);
  };

  const onEmployeeCountSave = () =>
    updateAccountData({
      employeeCount,
      yearlyEmployeeCounts: {
        ...yearlyEmployeeCounts,
        [selectedYear]: employeeCount
      }
    });

  const onIndustrySave = () =>
    updateAccountData({
      industry,
      yearlyIndustry: { ...yearlyIndustry, [selectedYear]: industry }
    });

  const onAddressEdit = async (place) => {
    const { place_id, description, formatted_address } = place;
    const address = await findGoogleAddressStringDetails(place_id);

    const eGrid = await fetchAddressEGrid(address);
    return editCompanyData("address")({
      ...address,
      description: description || formatted_address,
      placeId: place_id,
      eGrid
    });
  };

  const addressStep = isRemote
    ? [
        {
          field: "address",
          fieldId: SURVEY_FIELD_IDS.address,
          prompt:
            "Even if everyone's remote, your business has a primary address on file somewhere. What is it?",
          title: "Business Address",
          action: {
            title:
              "Tip: Even if your company is fully remote, you still have a business address."
          },
          formProps: {
            title: "What is your company's business address?",
            saveDisabled: !address
          },
          form: (
            <PlacesAutocomplete
              label="Business Address"
              place={address}
              editPlace={onAddressEdit}
            />
          )
        }
      ]
    : [];

  const vehiclesQuestion = [
    {
      field: "hasNoCompanyVehicles",
      fieldId: SURVEY_FIELD_IDS.hasNoCompanyVehicles,
      prompt:
        "Does your company own or lease any vehicles? Delivery vans, trucks, and company cars all count.",
      title: "Vehicles",
      description:
        "The fuel used for company vehicles is important information needed to calculate your carbon footprint, and you'll be asked for more detailed information in upcoming questions.",
      formProps: {
        title: "Did your company own any vehicles?",
        saveDisabled: hasNoCompanyVehicles === undefined
      },
      form: (
        <YesNoCheckboxes
          value={
            hasNoCompanyVehicles !== undefined
              ? !hasNoCompanyVehicles
              : undefined
          }
          setValue={(value) =>
            editCompanyData("hasNoCompanyVehicles")(!value)
          }
        />
      )
    }
  ];

  const wrapStep = (defaultSection) => (
    { form, formProps, field, fieldId, prompt, section, ...otherProps },
    idx
  ) => {
    const stepSection = section || defaultSection;
    return {
      ...otherProps,
      fieldId,
      prompt,
      field,
      value: vendorData[field],
      onSave: formProps?.onSave,
      isComplete: vendorData[field] !== undefined,
      form: (
        <FormDateTitleLayout
          {...formProps}
          field={field}
          fieldId={fieldId}
          value={vendorData[field]}
          section={stepSection}
          onNextStep={onNextStep}
          onPrevStep={idx && onPrevStep}
        >
          {form}
        </FormDateTitleLayout>
      ),
      section: stepSection
    };
  };

  const companyDataInputSteps = [
    {
      field: "industry",
      fieldId: SURVEY_FIELD_IDS.industry,
      prompt:
        "Let's start with the basics — what does your company do? A short description like 'we make industrial soap' or 'we run a marketing agency' works great.",
      title: "Company Industry",
      description:
        "Please select your company's industry in the dropdown. We use this information to estimate your emissions when more complete information is unavailable as you progress through the survey.",
      formProps: {
        title: "What is your company's primary industry?",
        saveDisabled: !industry,
        onSave: onIndustrySave
      },
      form: (
        <IndustryAutocomplete
          industry={industry}
          setIndustry={(value) => {
            editCompanyData("industry")(value);
            editCompanyData("yearlyIndustry")({
              ...yearlyIndustry,
              [selectedYear]: value
            });
          }}
          fetchNaicsCodes={fetchNaicsCodes}
        />
      )
    },
    {
      field: "revenueYears",
      fieldId: SURVEY_FIELD_IDS.revenue,
      prompt: `Roughly how much revenue did your company bring in during ${selectedYear}? A ballpark is fine — your finance team would know the exact number if it matters.`,
      title: "Revenue",
      description:
        "It's important to tie your emissions to your revenue to arrive at the amount of carbon emissions your business has in relation to each dollar of revenue. In addition, this information can be used to estimate emissions where more detailed data is not available.",
      action: {
        title: "How to find this information",
        subtitle: "Ask your finance team"
      },
      formProps: {
        title: "What was your company's total revenue?",
        saveDisabled: !revenue || !numbersRegExpTest(revenue) || revenue <= 0
      },
      form: <DollarInput value={revenue} setValue={onRevenueEdit} />
    },
    {
      field: "employeeCount",
      fieldId: SURVEY_FIELD_IDS.employees,
      prompt: `How many people worked at the company in ${selectedYear}? Full-time and part-time together — just a total headcount.`,
      title: "Employees",
      description:
        "The emissions from your employees \u2013 both remote and in-office workers \u2013 are part of the full picture of your business emissions.",
      action: {
        title: "How to find this information",
        subtitle: "Ask HR or management"
      },
      formProps: {
        title: "How many employees did your company have?",
        saveDisabled: !employeeCount || !numbersRegExpTest(employeeCount),
        onSave: onEmployeeCountSave
      },
      form: (
        <EmployeeCount
          employeeCount={employeeCount}
          setEmployeeCount={(value) => {
            editCompanyData("employeeCount")(value);
            editCompanyData("yearlyEmployeeCounts")({
              ...yearlyEmployeeCounts,
              [selectedYear]: value
            });
          }}
        />
      )
    },
    {
      field: "isRemote",
      fieldId: SURVEY_FIELD_IDS.isRemote,
      prompt:
        "Now let's talk about your workspace — does the company have any physical offices or facilities, or is everyone fully remote?",
      title: "Offices",
      formProps: {
        title: "Did your company have company operated facilities?",
        saveDisabled: isRemote === undefined
      },
      action: {
        title:
          "Tip: Check the \u201CNo\u201D box if your business does not own or lease any physical work space and all of your employees all work from home."
      },
      form: (
        <YesNoCheckboxes
          value={isRemote !== undefined ? !isRemote : undefined}
          setValue={(value) => editCompanyData("isRemote")(!value)}
        />
      )
    },
    ...addressStep
  ];

  return {
    companyDataSteps: companyDataInputSteps.map(
      wrapStep(SECTION_IDS.COMPANY_PROFILE)
    ),
    vehiclesSteps: vehiclesQuestion.map(wrapStep(SECTION_IDS.VEHICLES_FUEL))
  };
};

const useFacilitiesInputsStep = () => {
  const {
    facilities,
    setFacilities,
    vendorData,
    onNextStep,
    onPrevStep,
    scopeTons,
    selectedYear,
    newCollectionDoc,
    updateCollectionDoc,
    deleteCachedCollectionDoc
  } = useContext(SurveyContext);
  const { isRemote } = vendorData;
  const [existingOffices] = useCachedDisplayData("offices");

  const scope1Tons = extractScopeTons(scopeTons, 1);
  const scope2Tons = extractScopeTons(scopeTons, 2);

  const onFacilitiesSave = async () => {
    const currentIds = new Set(
      facilities.filter(({ id }) => id).map(({ id }) => id)
    );

    const isSurveyOwnedOffice = (office) => !office?.source;

    const idsToDelete = (existingOffices || [])
      .filter(
        (office) => isSurveyOwnedOffice(office) && !currentIds.has(office.id)
      )
      .map(({ id }) => id);

    await Promise.all(
      idsToDelete.map((id) => deleteCachedCollectionDoc("offices", id))
    );

    await Promise.all(
      facilities.map(({ id, ...data }) => {
        if (id) {
          return updateCollectionDoc("offices", id, data);
        }
        return newCollectionDoc("offices", data);
      })
    );
  };

  if (isRemote) {
    return [];
  }

  const hasBothFacilitiesScopeTotals = scope1Tons > 0 && scope2Tons > 0;
  if (hasBothFacilitiesScopeTotals) {
    return [];
  }

  const facilitiesFieldId = SURVEY_FIELD_IDS.facility(0);

  return [
    {
      isComplete: !isObjectEmpty(facilities[0]),
      fieldId: facilitiesFieldId,
      prompt:
        "Tell me about your first facility — what do you call it and where is it? We'll get the size and building type next, and you can add more facilities after.",
      title: "Facility Information",
      description:
        "The more information you're able to provide about your facilities including offices, the better \u2013 location, square footage, building type, kilowatt hours (kWh) and more. More complete information will give you a more accurate picture of your emissions using what's called the \u201Cactivity-based\u201D method. If you don't have all of the information available, we can develop a \u201Cspend-based\u201D estimate based on your costs. If you don't have your utility information, we can use your square footage and calculate this based on the average in your area. And remember, you can always come back and add more information later.",
      action: {
        title: "How to find this information",
        subtitle: "Ask your landlord or office manager"
      },
      onSave: onFacilitiesSave,
      form: (
        <FacilitiesInputBlock
          onNextStep={onNextStep}
          onPrevStep={onPrevStep}
          facilities={facilities}
          setFacilities={setFacilities}
          onSave={onFacilitiesSave}
          scope1Tons={scope1Tons}
          scope2Tons={scope2Tons}
          selectedYear={selectedYear}
          fieldId={facilitiesFieldId}
        />
      ),
      section: SECTION_IDS.FACILITIES_ENERGY
    }
  ];
};

const useTransactionsSteps = () => {
  const {
    transactions,
    setTransactions,
    vehicleFuelData,
    setVehicleFuelData,
    onNextStep,
    onPrevStep,
    vendorData,
    facilities,
    scopeTons,
    selectedYear,
    newCollectionDoc,
    deleteCachedCollectionDoc
  } = useContext(SurveyContext);
  const [existingTransactions] = useCachedDisplayData("transactions");

  const { address, hasNoCompanyVehicles } = vendorData;
  const companyAddress = address || facilities[0]?.address;

  const hasScopeOneTotal = extractScopeTons(scopeTons, 1) > 0;
  const hasScopeThreeTotal = extractScopeTons(scopeTons, 3) > 0;

  const {
    fuelVolume,
    fuelVolumeUnit = "gallons",
    fuelDollars
  } = vehicleFuelData;

  const editVehicleFuelData = (field) => (value) =>
    editObjectData(setVehicleFuelData, field, value);

  const onVehicleFuelSubmit = async () => {
    const findGallons = () =>
      convertFuelVolumeToGallons(fuelVolume, fuelVolumeUnit);

    const fetchTransactionTonsData = async () => {
      if (fuelVolume && fuelVolumeUnit) {
        return fuelCarbonFromGallons(findGallons());
      }

      return await fetchFuelBillCarbon({
        billValue: fuelDollars,
        gasStation: companyAddress
      });
    };

    const tonsCo2e = await fetchTransactionTonsData();

    const existingFuel = existingTransactions?.find(
      ({ subcategory, source, date }) => {
        const d = date?.toDate ? date.toDate() : date;
        return (
          subcategory === "fuel" &&
          source === "vendor-survey" &&
          new Date(d).getFullYear() === Number(selectedYear)
        );
      }
    );
    if (existingFuel) {
      await deleteCachedCollectionDoc("transactions", existingFuel.id);
    }

    await newCollectionDoc("transactions", {
      archived: false,
      source: "vendor-survey",
      status: "confirmed",
      name: `${selectedYear} Vehicle Fuel`,
      subcategory: "fuel",
      date: new Date(selectedYear, 11, 30),
      tonsCo2e,
      billGallons: findGallons(),
      fuelCost: fuelDollars
    });
  };

  const isVehicleFuelQuestionComplete = () => {
    if (!Array.isArray(transactions)) {
      return false;
    }

    return !!transactions?.find(({ subcategory }) => subcategory === "fuel");
  };

  const vehicleFuelQuestion =
    !hasNoCompanyVehicles && !hasScopeOneTotal
      ? [
          {
            isComplete: isVehicleFuelQuestionComplete(),
            fieldId: SURVEY_FIELD_IDS.vehicleFuel,
            prompt: `Roughly how much fuel did your vehicles use in ${selectedYear}? Gallons, liters, or just total spent — whichever's easiest.`,
            title: "Vehicle Information",
            description:
              "As we explained for your facilities, the most accurate method of calculating your emissions is \u201Cactivity based\u201D, so for your company-owned vehicles, this means entering the amount of fuel used in gallons. If this information is not available, we can estimate using the amount spent on fuel.",
            action: {
              title: "How to find this information",
              subtitle: "This can often be found under reimbursements"
            },
            onSave: onVehicleFuelSubmit,
            form: (
              <VehicleFuelQuestion
                onNextStep={onNextStep}
                onPrevStep={onPrevStep}
                editVehicleFuelData={editVehicleFuelData}
                onSave={onVehicleFuelSubmit}
                fieldId={SURVEY_FIELD_IDS.vehicleFuel}
                {...vehicleFuelData}
              />
            ),
            section: SECTION_IDS.VEHICLES_FUEL
          }
        ]
      : [];

  const { updateAccountData: updateAccountDataForTransactions } =
    useContext(SurveyContext);

  const transactionsCategorySteps = transactionsQuestions.map((step, idx) => {
    const {
      title,
      description,
      scopeThreeCategory,
      questions: stepQuestions,
      naics: stepCategoryNaics,
      tonsCo2ePerDollar: stepCategoryTonsCo2ePerDollar
    } = step;
    const stepFieldId = SURVEY_FIELD_IDS.transactions(scopeThreeCategory);
    const categoryPromptIntro = {
      "Business Travel": `What about business travel — flights, hotels, train tickets, car rentals. Roughly what did your company spend on travel in ${selectedYear}?`,
      "Transportation/Freight": `How about freight or shipping — moving products or materials around. Roughly what did you spend on this in ${selectedYear}?`,
      "Capital Goods": `Capital goods are the durable stuff your business buys — furniture, equipment, machinery, computers. Roughly what did you spend on these in ${selectedYear}?`,
      Services: `Last category — services. Things like legal, accounting, software subscriptions, insurance, banking. Roughly what did you spend on these in ${selectedYear}?`
    };
    const categoryPrompt =
      categoryPromptIntro[title] ||
      `Roughly what did your company spend on ${title.toLowerCase()} in ${selectedYear}? A ballpark is fine.`;

    const stepOnSave = buildTransactionCategorySave({
      transaction: transactions[idx],
      questions: stepQuestions,
      categoryNaics: stepCategoryNaics,
      categoryTonsCo2ePerDollar: stepCategoryTonsCo2ePerDollar,
      scopeThreeCategory,
      title,
      selectedYear,
      existingTransactions,
      newCollectionDoc,
      deleteCachedCollectionDoc,
      updateAccountData: updateAccountDataForTransactions
    });

    return {
      isComplete: !isObjectEmpty(transactions[idx]),
      fieldId: stepFieldId,
      prompt: categoryPrompt,
      title,
      description,
      onSave: stepOnSave,
      form: (
        <TransactionsQuestionsLayout
          {...step}
          onNextStep={onNextStep}
          onPrevStep={onPrevStep}
          transaction={transactions[idx]}
          title={title}
          fieldId={stepFieldId}
          editTransactionRow={editRowData(idx, setTransactions)}
        />
      ),
      section: sectionForTransactionCategory(title)
    };
  });

  const transactionsQuestionsSteps = hasScopeThreeTotal
    ? []
    : transactionsCategorySteps;

  return [...vehicleFuelQuestion, ...transactionsQuestionsSteps];
};

const buildSectionJumpTargets = (answerableSteps, answerableStartIndex) =>
  SURVEY_SECTIONS.filter(({ id }) => id !== SECTION_IDS.TOTALS_REVIEW)
    .map((section) => {
      const relativeIndex = answerableSteps.findIndex(
        (step) => step.section === section.id
      );
      if (relativeIndex < 0) {
        return null;
      }
      return {
        ...section,
        targetStepIndex: answerableStartIndex + relativeIndex
      };
    })
    .filter((entry) => entry !== null);

const ReturningUserCategorySelector = ({ sectionJumpTargets }) => {
  const {
    selectedYear,
    startEditingField,
    setIsScoutCollapsed
  } = useContext(SurveyContext);

  useEffect(() => {
    if (setIsScoutCollapsed) {
      setIsScoutCollapsed(true);
    }
  }, [setIsScoutCollapsed]);

  const handleSectionSelect = (targetStepIndex) => () => {
    if (startEditingField) {
      startEditingField(targetStepIndex);
    }
  };

  return (
    <Grid container direction="column" spacing={4}>
      <Grid item>
        <DateRangeText />
        <Typography variant="h3">
          Which category would you like to update?
        </Typography>
        <Typography variant="body1" color="textSecondary">
          You've already completed your {selectedYear} emissions survey. Pick a
          category to jump straight to those questions — we'll bring you back
          here when you're done so you can update another.
        </Typography>
      </Grid>
      <Grid item container direction="column" spacing={2}>
        {sectionJumpTargets.map(({ id, label, targetStepIndex }) => (
          <Grid item key={`category-selector-${id}`}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleSectionSelect(targetStepIndex)}
            >
              {label}
            </Button>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

const buildYearSelectorPrompt = (availableYears, vendorSurveys) => {
  if (!availableYears.length) {
    return "Which year would you like to complete your emissions survey for?";
  }
  const completedYears = new Set(
    (vendorSurveys || []).map(({ year }) => Number(year))
  );
  const yearLabels = availableYears.map((year) =>
    completedYears.has(Number(year)) ? `${year} (update existing)` : `${year}`
  );
  if (yearLabels.length === 1) {
    return `Which year would you like to estimate your emissions for?`;
  }
  const last = yearLabels[yearLabels.length - 1];
  const head = yearLabels.slice(0, -1).join(", ");
  return `Which year would you like to work on — ${head} or ${last}?`;
};

const isStepAnswered = (step, context, skippedFields, unknownFields) => {
  const { fieldId } = step;
  if (!fieldId) {
    return false;
  }
  if (getFieldDisplayValue(fieldId, context)) {
    return true;
  }
  if (skippedFields.includes(fieldId)) {
    return true;
  }
  if (unknownFields.includes(fieldId)) {
    return true;
  }
  return false;
};

const stepHasActualValue = (step, context) => {
  if (!step.fieldId) {
    return false;
  }
  return !!getFieldDisplayValue(step.fieldId, context);
};

const useEmissionsSurveyFormSteps = () => {
  const context = useContext(SurveyContext);
  const { selectedYear, vendorData } = context;
  const { vendorSurveys = [] } = vendorData || {};

  const scopeQuestionStep = useScopeQuestionsStep();
  const { companyDataSteps, vehiclesSteps } = useVendorDataSteps();
  const facilitiesForm = useFacilitiesInputsStep();
  const transactionsSteps = useTransactionsSteps();

  const availableYears = getAvailableSurveyYears(vendorSurveys);
  const yearSelectorPrompt = buildYearSelectorPrompt(
    availableYears,
    vendorSurveys
  );

  const yearStep = [
    {
      isComplete: !!selectedYear,
      fieldId: SURVEY_FIELD_IDS.yearSelector,
      prompt: yearSelectorPrompt,
      title: "Emissions Profile Survey",
      content: <IntroductionContentBlock />,
      description:
        "Complete this survey to build your company's emissions profile for a past calendar year.",
      action: {
        title:
          "Select the year you want to report on. You can complete surveys for multiple years consecutively."
      },
      form: <YearSelector />,
      section: SECTION_IDS.COMPANY_PROFILE
    }
  ];

  const answerableSteps = [
    ...scopeQuestionStep,
    ...companyDataSteps,
    ...facilitiesForm,
    ...vehiclesSteps,
    ...transactionsSteps
  ];

  const hasSurveyEstimateForYear = hasSubmittedSurveyForYear(
    vendorSurveys,
    selectedYear
  );
  const answerableStepsAllComplete =
    answerableSteps.length > 0 &&
    answerableSteps.every((step) => stepHasActualValue(step, context));
  const isSurveyFullyCompleteForYear =
    hasSurveyEstimateForYear && answerableStepsAllComplete;

  const answerableStartIndex =
    yearStep.length + (isSurveyFullyCompleteForYear ? 1 : 0);

  const sectionJumpTargets = buildSectionJumpTargets(
    answerableSteps,
    answerableStartIndex
  );

  const categorySelectorStep = isSurveyFullyCompleteForYear
    ? [
        {
          isComplete: false,
          title: "Update Your Survey",
          prompt: `You've already completed your ${selectedYear} emissions survey — which category would you like to update?`,
          description:
            "Pick a category and we'll jump straight there. When you save that answer we'll bring you back here to pick another.",
          form: (
            <ReturningUserCategorySelector
              sectionJumpTargets={sectionJumpTargets}
            />
          ),
          section: SECTION_IDS.COMPANY_PROFILE
        }
      ]
    : [];

  const steps = [
    ...yearStep,
    ...categorySelectorStep,
    ...answerableSteps,
    {
      isComplete: false,
      title: "Thanks",
      isFinalStep: true,
      form: <ThanksStep />,
      section: SECTION_IDS.TOTALS_REVIEW
    }
  ];

  return { steps, isSurveyFullyCompleteForYear };
};

const findFirstIncompleteStepIndex = (steps, context, skippedFields, unknownFields) => {
  const idx = steps.findIndex((step) => {
    if (step.isFinalStep) {
      return true;
    }
    return !isStepAnswered(step, context, skippedFields, unknownFields);
  });
  return idx < 0 ? 0 : idx;
};

const EmissionsSurveyFormSteps = ({ surveyLoading, error }) => {
  const context = useContext(SurveyContext);
  const {
    formStep,
    setFormStep,
    formStepsCount,
    setFormStepsCount,
    updateAccountData,
    registerStepSubmit,
    selectedYear,
    vendorId,
    skippedFields = [],
    unknownFields = [],
    dbDataLoading
  } = context;
  const onSurveyComplete = useOnSurveyComplete();

  const { steps: surveySteps, isSurveyFullyCompleteForYear } =
    useEmissionsSurveyFormSteps();
  const { setSurveyCompletionStatus } = context;

  const initialStepJumpAppliedRef = useRef(false);
  const hasHydratedVendorData =
    !!context.vendorData && Object.keys(context.vendorData).length > 0;
  const [offices, officesLoading] = useCachedDisplayData("offices");
  const hasHydratedFacilities =
    !officesLoading &&
    ((Array.isArray(offices) && offices.length > 0) ||
      context.vendorData?.isRemote !== undefined);
  const isReturningUserForSelectedYear = isSurveyFullyCompleteForYear;
  useEffect(() => {
    if (initialStepJumpAppliedRef.current) {
      return;
    }
    if (
      dbDataLoading ||
      !selectedYear ||
      !surveySteps.length ||
      !hasHydratedVendorData
    ) {
      return;
    }
    if (isReturningUserForSelectedYear) {
      initialStepJumpAppliedRef.current = true;
      if (formStep === 0) {
        setFormStep(1);
      }
      return;
    }
    if (!hasHydratedFacilities) {
      return;
    }
    initialStepJumpAppliedRef.current = true;
    const nextStep = findFirstIncompleteStepIndex(
      surveySteps,
      context,
      skippedFields,
      unknownFields
    );
    if (nextStep > 0 && nextStep !== formStep) {
      setFormStep(nextStep);
    }
  }, [
    dbDataLoading,
    selectedYear,
    surveySteps,
    context,
    skippedFields,
    unknownFields,
    formStep,
    setFormStep,
    hasHydratedVendorData,
    hasHydratedFacilities,
    isReturningUserForSelectedYear
  ]);

  useEffect(() => {
    if (formStepsCount !== surveySteps.length) {
      setFormStepsCount(surveySteps.length);
    }
    const clamped = clampFormStepToBounds(formStep, surveySteps.length);
    if (clamped !== formStep) {
      setFormStep(clamped);
    }
  }, [surveySteps, formStepsCount, setFormStepsCount, formStep, setFormStep]);

  const currentStepForRegistration = surveySteps[formStep] || {};
  const currentStepOnSave = currentStepForRegistration.onSave;
  const currentStepField = currentStepForRegistration.field;
  const currentStepValue = currentStepForRegistration.value;

  useEffect(() => {
    if (!registerStepSubmit) {
      return undefined;
    }
    if (!currentStepForRegistration.fieldId) {
      registerStepSubmit(null);
      return undefined;
    }
    const isFinalStep = formStep === surveySteps.length - 2;
    const saveFn = async () => {
      if (currentStepOnSave) {
        await currentStepOnSave();
      }
      if (
        !currentStepOnSave &&
        currentStepField &&
        currentStepValue !== undefined
      ) {
        await updateAccountData({ [currentStepField]: currentStepValue });
      }
      if (isFinalStep) {
        if (setSurveyCompletionStatus) {
          setSurveyCompletionStatus("in-flight");
        }
        onSurveyComplete()
          .then(() => {
            if (setSurveyCompletionStatus) {
              setSurveyCompletionStatus("done");
            }
          })
          .catch((err) => {
            Sentry.captureException(err, {
              tags: { feature: "explorer-survey-complete", path: "scout-advance" },
              extra: { surveyYear: selectedYear, vendorId }
            });
            if (setSurveyCompletionStatus) {
              setSurveyCompletionStatus("error");
            }
          });
      }
    };
    registerStepSubmit(saveFn);
    return () => registerStepSubmit(null);
  }, [
    registerStepSubmit,
    currentStepForRegistration.fieldId,
    currentStepOnSave,
    currentStepField,
    currentStepValue,
    formStep,
    surveySteps.length,
    updateAccountData,
    onSurveyComplete,
    setSurveyCompletionStatus,
    selectedYear,
    vendorId
  ]);

  const currentStep = surveySteps[formStep] || {};

  const surveyStepMeta = surveySteps
    .map((step, idx) => ({
      stepIndex: idx,
      fieldId: step.fieldId,
      title: step.title
    }))
    .filter(({ fieldId, title }) => !!fieldId && title !== "Thanks");

  const scoutPanelCurrentStep =
    currentStep.fieldId || currentStep.title
      ? {
          fieldId: currentStep.fieldId,
          title: currentStep.title,
          prompt:
            currentStep.prompt || currentStep.description || currentStep.title,
          action: currentStep.action?.title,
          isFinalStep: !!currentStep.isFinalStep
        }
      : null;

  return (
    <SurveyLayout
      percentCompleted={formStep / surveySteps.length}
      sections={SURVEY_SECTIONS}
      activeSectionId={currentStep.section}
      surveyLoading={surveyLoading}
      error={
        error && {
          message:
            "Something went wrong loading the survey. Please try again or contact support."
        }
      }
      form={currentStep.form}
      scoutPanel={<SurveyScoutPanel currentStep={scoutPanelCurrentStep} />}
      summaryPane={<SurveyCapturedSummary surveyStepMeta={surveyStepMeta} />}
      mobileSummaryPaneBuilder={({ onCloseMobile }) => (
        <SurveyCapturedSummary
          surveyStepMeta={surveyStepMeta}
          onCloseMobile={onCloseMobile}
          isMobileDrawer
        />
      )}
    />
  );
};

const FormStepsStates = ({ isLoading, error }) => {
  const { dbDataLoading } = useContext(SurveyContext);
  const surveyLoading = isLoading || dbDataLoading;

  if (surveyLoading) {
    return <SurveyLayout surveyLoading />;
  }

  if (error) {
    return <SurveyLayout error={error} />;
  }

  return (
    <EmissionsSurveyFormSteps error={error} surveyLoading={surveyLoading} />
  );
};
export default FormStepsStates;
